import Box from "@mui/material/Box";
import GalleryItem from "./GalleryItem";
import Masonry from "@mui/lab/Masonry";
import Typography from "@mui/material/Typography";
import { useTranslationApp } from "../../lib/i18next";
import { MODAL_GALLERY_NO_MORE_CONTENT } from "../../i18n/keysTranslations";
import Loader from "../Loaders/Loader";
import React, { useMemo, memo, useState, useCallback } from "react";
import { getGalleryItemSelectionId } from "../../utils/gallerySelection";

const Gallery = memo(
  ({
    data = [],
    onCloseModal = () => {},
    lastItemRef,
    loadLastItem,
    onFailedLoadUrl = () => {},
    onSelectItem = () => {},
    onSelectPreview = () => {},
    onCallbackResizeFormat = () => {},
    onAnimateImage = () => {},
    onDeleteItem = () => {},
    onDownloadItems = () => {},
    selectedItems = [],
    multiple,
    from,
    acceptedFormats = [],
    minDimensions = {},
    showDimensionsOverlay = true,
    showActionButtons = true,
    onEditWithCanva,
    onEditWithIA,
    itemGeneratingComponent,
    sxGallery = {},
    showEmptyMessage = true,
    isLoading = false,
    showRating = false,
    showMetaAdDuration = false,
    showResizeFormatButton = false,
    showEditButton,
    showDeleteButton = true,
    showPreviewExpandOnly = false,
    showExpandButton = false,
    showFooterEditButton = false,
    onFooterEdit = () => {},
    showMetaAdLibraryLinkButton = false,
    showCompetitorFavoriteButton = false,
    getIsCompetitorFavorite = () => false,
    getIsCompetitorFavoriteLoading = () => false,
    onToggleCompetitorFavoriteItem = () => {},
    overlayPickerMode = false,
    loadingItems,
    loadingComponent,
    columns = { xs: 1, sm: 2, md: 3, lg: 4, xl: 5 },
    propsMedia = {},
    itemDimensions,
    scheduledDate = null,
    fromCalendar = false,
    selectedItemsArray = [],
    selectionBy = "index",
    showFeedbackButtons = false,
    onFeedback,
    showReportButton = false,
    showPostButton = true,
    showDirectAddButton = false,
    onDirectAddItem = () => {},
    directAddLabel,
    isDirectAddLoading = false,
    directAddLoadingIndex = null,
    onRefreshCalendar,
    onScheduledShareSuccess,
    showContentApproval = false,
    getContentApprovalData = () => null,
    enablingContentApprovalId = null,
    showContentApprovalEnableButton = true,
    onEnableContentApproval = () => {},
    onCopyContentApprovalLink = () => {},
    onOpenContentApprovalReview = () => {},
    showShimmer = false,
    highlightedIds = [],
    draggableForFolders = false,
    selectedItemsCount = 0,
  }) => {
    const { t } = useTranslationApp();
    const [unmutedVideoKey, setUnmutedVideoKey] = useState(null);

    const onRequestVideoUnmute = useCallback(({ videoMuteKey }) => {
      setUnmutedVideoKey(videoMuteKey);
    }, []);

    const onRequestVideoMute = useCallback(() => {
      setUnmutedVideoKey(null);
    }, []);

    const onSelectPreviewWithMute = useCallback(
      (indexOrData) => {
        setUnmutedVideoKey(null);
        onSelectPreview(indexOrData);
      },
      [onSelectPreview]
    );

    // Memoizar los items filtrados para evitar recalcular en cada render
    const validItems = useMemo(() => {
      return data
        .map((item, index) => ({ item, originalIndex: index }))
        .filter(({ item }) => (item && item.url) || item.isGenerating);
    }, [data]);

    const lastValidOriginalIndex = useMemo(() => {
      if (validItems.length === 0) return -1;
      return validItems[validItems.length - 1].originalIndex;
    }, [validItems]);

    const highlightedIdSet = useMemo(() => {
      if (!Array.isArray(highlightedIds) || highlightedIds.length === 0) {
        return new Set();
      }
      return new Set(highlightedIds.map((value) => String(value)));
    }, [highlightedIds]);

    const isItemHighlighted = useCallback(
      (galleryItem) => {
        if (highlightedIdSet.size === 0 || !galleryItem) {
          return false;
        }

        const candidateKeys = [
          galleryItem.id,
          galleryItem.stableKey,
          galleryItem.url,
        ]
          .filter(Boolean)
          .map((value) => String(value));

        return candidateKeys.some((key) => highlightedIdSet.has(key));
      },
      [highlightedIdSet]
    );

    return (
      <Box sx={{ pr: 4, pl: 5, ...sxGallery, overflowAnchor: "none" }}>
        {data?.length === 0 && !loadLastItem && (
          <div
            ref={lastItemRef}
            style={{ minHeight: 1, width: "100%" }}
            aria-hidden="true"
          />
        )}
        <Masonry columns={columns} spacing={2}>
          {isLoading &&
            loadingComponent &&
            loadingItems &&
            Array.from({ length: loadingItems }).map((_, index) => (
              <Box key={index}>{loadingComponent.component}</Box>
            ))}
          {validItems.length > 0 &&
            validItems.map(({ item, originalIndex }) => {
              const uniqueKey =
                item.stableKey ||
                item.id ||
                item.url ||
                `index-${item.creationTime || originalIndex}`;

              if (item.isGenerating) {
                return (
                  <Box key={uniqueKey} sx={{ flexShrink: 0 }}>
                    {itemGeneratingComponent &&
                      React.createElement(itemGeneratingComponent.component, {
                        data: item,
                        ...itemGeneratingComponent.props,
                      })}
                  </Box>
                );
              }
              const itemSelectionId =
                selectionBy === "id" ? getGalleryItemSelectionId(item) : null;

              return (
                <Box key={uniqueKey} sx={{ flexShrink: 0 }}>
                  <GalleryItem
                    item={item}
                    index={originalIndex}
                    onCloseModalGallery={onCloseModal}
                    onFailedLoadUrl={onFailedLoadUrl}
                    onSelectItem={onSelectItem}
                    onSelectPreview={onSelectPreviewWithMute}
                    onCallbackResizeFormat={onCallbackResizeFormat}
                    onAnimateImage={onAnimateImage}
                    onDeleteItem={onDeleteItem}
                    onDownloadItems={onDownloadItems}
                    multiple={multiple}
                    isSelected={
                      selectionBy === "id"
                        ? Boolean(
                            itemSelectionId &&
                              (selectedItems instanceof Set
                                ? selectedItems.has(itemSelectionId)
                                : selectedItems.includes(itemSelectionId))
                          )
                        : selectedItems instanceof Set
                          ? selectedItems.has(originalIndex)
                          : selectedItems.includes(originalIndex)
                    }
                    from={from}
                    acceptedFormats={acceptedFormats}
                    minDimensions={minDimensions}
                    showResizeFormatButton={showResizeFormatButton}
                    showEditButton={showEditButton}
                    showPreviewExpandOnly={showPreviewExpandOnly}
                    showExpandButton={showExpandButton}
                    showFooterEditButton={showFooterEditButton}
                    onFooterEdit={onFooterEdit}
                    showMetaAdLibraryLinkButton={showMetaAdLibraryLinkButton}
                    showCompetitorFavoriteButton={showCompetitorFavoriteButton}
                    isCompetitorFavorite={getIsCompetitorFavorite(item)}
                    isCompetitorFavoriteLoading={getIsCompetitorFavoriteLoading(
                      item
                    )}
                    onToggleCompetitorFavorite={() =>
                      onToggleCompetitorFavoriteItem({
                        item,
                        index: originalIndex,
                      })
                    }
                    showDimensionsOverlay={showDimensionsOverlay}
                    showActionButtons={showActionButtons}
                    showDeleteButton={showDeleteButton}
                    overlayPickerMode={overlayPickerMode}
                    onEditWithCanva={onEditWithCanva}
                    onEditWithIA={onEditWithIA}
                    propsMedia={propsMedia}
                    itemDimensions={itemDimensions}
                    showRating={showRating}
                    showMetaAdDuration={showMetaAdDuration}
                    scheduledDate={scheduledDate}
                    fromCalendar={fromCalendar}
                    selectionNumber={
                      selectionBy === "id"
                        ? itemSelectionId &&
                          selectedItemsArray.includes(itemSelectionId)
                          ? selectedItemsArray.indexOf(itemSelectionId) + 1
                          : null
                        : selectedItemsArray.includes(originalIndex)
                          ? selectedItemsArray.indexOf(originalIndex) + 1
                          : null
                    }
                    showFeedbackButtons={showFeedbackButtons}
                    onFeedback={onFeedback}
                    showReportButton={showReportButton}
                    showPostButton={showPostButton}
                    showDirectAddButton={showDirectAddButton}
                    directAddLabel={directAddLabel}
                    onDirectAddItem={onDirectAddItem}
                    isDirectAddLoading={isDirectAddLoading}
                    directAddLoadingIndex={directAddLoadingIndex}
                    onRefreshCalendar={onRefreshCalendar}
                    onScheduledShareSuccess={onScheduledShareSuccess}
                    showContentApproval={showContentApproval}
                    getContentApprovalData={getContentApprovalData}
                    enablingContentApprovalId={enablingContentApprovalId}
                    showContentApprovalEnableButton={
                      showContentApprovalEnableButton
                    }
                    onEnableContentApproval={onEnableContentApproval}
                    onCopyContentApprovalLink={onCopyContentApprovalLink}
                    onOpenContentApprovalReview={onOpenContentApprovalReview}
                    showShimmer={showShimmer}
                    unmutedVideoKey={unmutedVideoKey}
                    onRequestVideoUnmute={onRequestVideoUnmute}
                    onRequestVideoMute={onRequestVideoMute}
                    isHighlighted={isItemHighlighted(item)}
                    draggableForFolders={draggableForFolders}
                    itemSelectionId={itemSelectionId}
                    selectedItemsCount={selectedItemsCount}
                  />
                  {originalIndex === lastValidOriginalIndex && (
                    <div
                      ref={lastItemRef}
                      style={{ minHeight: 1, width: "100%" }}
                      aria-hidden="true"
                    />
                  )}
                </Box>
              );
            })}
        </Masonry>

        {isLoading &&
          loadingComponent &&
          !loadingItems &&
          loadingComponent.component}

        {!loadingComponent && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              mt: 2,
            }}
          >
            {!loadLastItem && <Loader size={30} sx={{ mb: 2 }} />}
            {loadLastItem && data?.length > 0 && showEmptyMessage && (
              <Typography variant="h6" sx={{ color: "white" }}>
                {t(MODAL_GALLERY_NO_MORE_CONTENT)}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.data !== nextProps.data) return false;
    if (prevProps.data?.length !== nextProps.data?.length) return false;
    if (prevProps.isLoading !== nextProps.isLoading) return false;
    if (prevProps.loadLastItem !== nextProps.loadLastItem) return false;
    if (prevProps.selectedItems !== nextProps.selectedItems) return false;
    if (prevProps.selectionBy !== nextProps.selectionBy) return false;
    if (prevProps.overlayPickerMode !== nextProps.overlayPickerMode)
      return false;
    if (prevProps.showDeleteButton !== nextProps.showDeleteButton) return false;
    if (prevProps.showEditButton !== nextProps.showEditButton) return false;
    if (prevProps.showPreviewExpandOnly !== nextProps.showPreviewExpandOnly)
      return false;
    if (prevProps.showExpandButton !== nextProps.showExpandButton)
      return false;
    if (prevProps.showFooterEditButton !== nextProps.showFooterEditButton)
      return false;
    if (
      prevProps.showMetaAdLibraryLinkButton !==
      nextProps.showMetaAdLibraryLinkButton
    )
      return false;
    if (
      prevProps.showCompetitorFavoriteButton !==
      nextProps.showCompetitorFavoriteButton
    )
      return false;
    if (prevProps.getIsCompetitorFavorite !== nextProps.getIsCompetitorFavorite)
      return false;
    if (
      prevProps.getIsCompetitorFavoriteLoading !==
      nextProps.getIsCompetitorFavoriteLoading
    )
      return false;
    if (
      prevProps.onToggleCompetitorFavoriteItem !==
      nextProps.onToggleCompetitorFavoriteItem
    )
      return false;
    if (prevProps.showReportButton !== nextProps.showReportButton) return false;
    if (prevProps.showPostButton !== nextProps.showPostButton) return false;
    if (prevProps.showDirectAddButton !== nextProps.showDirectAddButton)
      return false;
    if (prevProps.directAddLabel !== nextProps.directAddLabel) return false;
    if (prevProps.isDirectAddLoading !== nextProps.isDirectAddLoading)
      return false;
    if (prevProps.directAddLoadingIndex !== nextProps.directAddLoadingIndex)
      return false;
    if (prevProps.onDirectAddItem !== nextProps.onDirectAddItem) return false;
    if (prevProps.onRefreshCalendar !== nextProps.onRefreshCalendar)
      return false;
    if (prevProps.onScheduledShareSuccess !== nextProps.onScheduledShareSuccess)
      return false;
    if (prevProps.showRating !== nextProps.showRating) return false;
    if (prevProps.showMetaAdDuration !== nextProps.showMetaAdDuration)
      return false;
    if (prevProps.itemDimensions !== nextProps.itemDimensions) return false;
    if (
      prevProps.enablingContentApprovalId !==
      nextProps.enablingContentApprovalId
    )
      return false;
    if (prevProps.showContentApproval !== nextProps.showContentApproval)
      return false;
    if (
      prevProps.showContentApprovalEnableButton !==
      nextProps.showContentApprovalEnableButton
    )
      return false;
    if (prevProps.draggableForFolders !== nextProps.draggableForFolders)
      return false;
    if (prevProps.selectedItemsCount !== nextProps.selectedItemsCount)
      return false;

    if (prevProps.showShimmer !== nextProps.showShimmer) return false;
    if (
      JSON.stringify(prevProps.highlightedIds) !==
      JSON.stringify(nextProps.highlightedIds)
    ) {
      return false;
    }

    // Comparar arrays de selectedItemsArray
    if (
      prevProps.selectedItemsArray?.length !==
      nextProps.selectedItemsArray?.length
    )
      return false;
    if (
      JSON.stringify(prevProps.selectedItemsArray) !==
      JSON.stringify(nextProps.selectedItemsArray)
    )
      return false;

    const prevCols = prevProps.columns;
    const nextCols = nextProps.columns;
    if (
      prevCols?.xs !== nextCols?.xs ||
      prevCols?.sm !== nextCols?.sm ||
      prevCols?.md !== nextCols?.md ||
      prevCols?.lg !== nextCols?.lg ||
      prevCols?.xl !== nextCols?.xl
    ) {
      return false;
    }

    return true;
  }
);

Gallery.displayName = "Gallery";

export default Gallery;
